import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';
import { useLoader } from '../context/loaders/useLoader';
import { useToast } from '../context/toasts/useToast';
import type { Epic, CreateEpicRequest, UpdateEpicRequest } from '../types/epic';

function useEpics(boardId: string) {
  const [epics, setEpics] = useState<Epic[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const {
    getEpics,
    createEpic: apiCreateEpic,
    updateEpic: apiUpdateEpic,
    deleteEpic: apiDeleteEpic,
  } = useApi();
  const { withLoader } = useLoader();
  const { addNotification } = useToast();

  const fetchEpics = useCallback(async () => {
    try {
      setError(null);
      const data = await withLoader(() => getEpics(Number(boardId)), 'Loading epics...');
      setEpics(data);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load epics';
      setError(err instanceof Error ? err : new Error(errorMessage));
      addNotification({
        type: 'error',
        title: 'Error Loading Epics',
        message: errorMessage,
        duration: 5000,
      });
      return [];
    }
  }, [boardId, getEpics, withLoader, addNotification]);

  const createEpic = useCallback(
    async (epicData: CreateEpicRequest) => {
      try {
        setError(null);
        const newEpic = await withLoader(
          () => apiCreateEpic(Number(boardId), epicData),
          'Creating epic...'
        );
        setEpics((prev) => [...prev, newEpic]);
        addNotification({
          type: 'success',
          title: 'Epic Created',
          message: `Epic "${epicData.name}" created successfully.`,
          duration: 3000,
        });
        return newEpic;
      } catch (err: any) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create epic';
        setError(err instanceof Error ? err : new Error(errorMessage));

        if (err.status === 400 && err.response?.data?.validation_error?.body_params) {
          const validationErrors = err.response.data.validation_error.body_params;

          for (let index = 0; index < validationErrors.length; index++) {
            const validationError = validationErrors[index];

            const validationMessage = `${validationError.loc.join(', ')} ${validationError.msg}`;

            addNotification({
              type: 'error',
              title: 'Error Creating Epic',
              message: validationMessage,
              duration: 5000,
            });
          }
        } else {
          addNotification({
            type: 'error',
            title: 'Error Creating Epic',
            message: errorMessage,
            duration: 5000,
          });
        }
        throw err;
      }
    },
    [boardId, apiCreateEpic, withLoader, addNotification]
  );

  const updateEpic = useCallback(
    async (epicId: number, epicData: UpdateEpicRequest) => {
      try {
        setError(null);
        const updatedEpic = await withLoader(
          () => apiUpdateEpic(epicId, epicData),
          'Updating epic...'
        );
        setEpics((prev) => prev.map((epic) => (epic.id === epicId ? updatedEpic : epic)));
        addNotification({
          type: 'success',
          title: 'Epic Updated',
          message: 'Epic updated successfully.',
          duration: 3000,
        });
        return updatedEpic;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update epic';
        setError(err instanceof Error ? err : new Error(errorMessage));
        addNotification({
          type: 'error',
          title: 'Error Updating Epic',
          message: errorMessage,
          duration: 5000,
        });
        throw err;
      }
    },
    [apiUpdateEpic, withLoader, addNotification]
  );

  const deleteEpic = useCallback(
    async (epicId: number) => {
      try {
        setError(null);
        await withLoader(() => apiDeleteEpic(epicId), 'Deleting epic...');
        setEpics((prev) => prev.filter((epic) => epic.id !== epicId));
        addNotification({
          type: 'success',
          title: 'Epic Deleted',
          message: 'Epic deleted successfully.',
          duration: 3000,
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete epic';
        setError(err instanceof Error ? err : new Error(errorMessage));
        addNotification({
          type: 'error',
          title: 'Error Deleting Epic',
          message: errorMessage,
          duration: 5000,
        });
        throw err;
      }
    },
    [apiDeleteEpic, withLoader, addNotification]
  );

  useEffect(() => {
    fetchEpics();
  }, [fetchEpics]);

  return {
    epics,
    error, // For debugging, not for UI display
    loading: false, // Loading is handled by global loader
    fetchEpics,
    createEpic,
    updateEpic,
    deleteEpic,
    refetch: fetchEpics,
  };
}

export default useEpics;
